import { useCallback, useLayoutEffect } from "react"
import PropTypes from "prop-types"
import { useLocation, useNavigate } from "react-router-dom"
import { SEARCHABLE_MENU_ITEM, TABLE_GROUP_DROPDOWN, LIST_ITEM_TEXT_OPTION } from "helpers/shapes"
import { Paper } from "@mui/material"
import Table from "components/securities-table/table"
import AssetsTableHeader from "components/assets-table-header"
import { useCheckLastPreferenceRedirect, useSaveContextPreferences } from "hooks/preferences"
import "./styles.scss"

function AssetAnalyticsTable({
  aggregatedRow,
  columns,
  columnOrder,
  columnOrderOptions,
  count,
  downloadData,
  dispatch,
  downloadButtonTooltip,
  foundSecurities,
  groupDropdown,
  idSort,
  isActive,
  menuItems,
  metricType,
  metricTypeOptions,
  modelId,
  openFactorProfile,
  openSecurityProfile,
  openCompositeSecurity,
  page,
  selectedSubView,
  selectedView,
  setColumnOrder,
  setMetricType,
  setPage,
  setSelectedGroup,
  setSelectedSubView,
  setSelectedView,
  setSort,
  status,
  subViewOptions,
  isOpPro,
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const saveContextPreferences = useSaveContextPreferences("navigation.analyze.assets")
  const { wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault } = useCheckLastPreferenceRedirect()

  useLayoutEffect(() => {
    if (isOpPro && wasRedirectedFromPreferences && ["risk", "marginal", "predicted-factor-sensitivity"].includes(selectedView)) {
      deletePreferenceAndRedirectToDefault()
    }
  }, [isOpPro, wasRedirectedFromPreferences, deletePreferenceAndRedirectToDefault, selectedView])

  const handleSetSelectedView = useCallback((view) => {
    const { pathname, search } = location
    const pathParts = pathname.split("/", 6)

    saveContextPreferences(view)
    setSelectedView(view)
    navigate(`${pathParts.join("/")}/${view}${search}`)
  }, [location, saveContextPreferences, setSelectedView, navigate])

  return (
    <Paper square className="modules_asset-analytics-table">
      <AssetsTableHeader
        {...{
          columnOrder,
          downloadData,
          downloadButtonTooltip,
          menuItems,
          metricType,
          metricTypeOptions,
          setColumnOrder,
          selectedSubView,
          selectedView,
          setMetricType,
          setSelectedSubView,
          setSelectedView: handleSetSelectedView,
          subViewOptions,
          columnOrderOptions,
        }}
      />
      <Table
        {...{
          aggregatedRow,
          columns,
          columnOrder,
          count,
          disableColumnsSelection: true,
          disableRowSelection: true,
          dispatch,
          foundSecurities,
          groupDropdown,
          idSort,
          allowIdSort: true,
          isActive,
          modelId,
          openFactorProfile,
          openSecurityProfile,
          openCompositeSecurity,
          outsidePagination: true,
          page,
          rowIdClickOverride: (item) => setSelectedGroup(item.id),
          selectedSecurities: {},
          setPage,
          setSort,
          status,
        }}
      />
    </Paper>
  )
}

AssetAnalyticsTable.propTypes = {
  modelId: PropTypes.string,
  assetName: PropTypes.string,
  assetType: PropTypes.string,
  endDate: PropTypes.string.isRequired,
  aggregatedRow: PropTypes.object.isRequired,
  currentGrouping: PropTypes.object,
  columnOrder: PropTypes.string.isRequired,
  columnOrderOptions: PropTypes.arrayOf(LIST_ITEM_TEXT_OPTION),
  columns: PropTypes.array.isRequired,
  disableColumnClick: PropTypes.bool,
  count: PropTypes.number.isRequired,
  downloadData: PropTypes.object.isRequired,
  dispatch: PropTypes.func.isRequired,
  downloadButtonTooltip: PropTypes.string.isRequired,
  foundSecurities: PropTypes.array.isRequired,
  groupDropdown: TABLE_GROUP_DROPDOWN,
  idSort: PropTypes.string,
  isActive: PropTypes.bool.isRequired,
  menuItems: PropTypes.arrayOf(SEARCHABLE_MENU_ITEM),
  metricType: PropTypes.string,
  metricTypeOptions: PropTypes.arrayOf(LIST_ITEM_TEXT_OPTION),
  openFactorProfile: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  page: PropTypes.number.isRequired,
  selectedSubView: PropTypes.string,
  selectedView: PropTypes.string.isRequired,
  viewType: PropTypes.string,
  setColumnOrder: PropTypes.func.isRequired,
  setMetricType: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setSelectedGroup: PropTypes.func.isRequired,
  setSelectedSubView: PropTypes.func.isRequired,
  setSelectedView: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  status: PropTypes.string,
  subViewOptions: PropTypes.arrayOf(LIST_ITEM_TEXT_OPTION),
  hideColumnWith: PropTypes.func,
  isOpPro: PropTypes.bool,
}

export default AssetAnalyticsTable
